import { useState, useEffect } from "react";
import { Wrench, Droplets, Cpu, Snowflake, Gauge, CheckCircle2 } from "lucide-react";
import { ImageSlot } from "@/components/ImageSlot";
import { LeadDialog } from "@/components/public/LeadDialog";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";

const SERVICES = [
  { icon: Gauge, title: "Yamaha & Suzuki Service", desc: "Factory-trained outboard maintenance and diagnostics." },
  { icon: Wrench, title: "Repower", desc: "Upgrade to a new Yamaha or Suzuki outboard with expert rigging." },
  { icon: Snowflake, title: "Winterization", desc: "Protect your investment through the off-season." },
  { icon: Cpu, title: "Electronics", desc: "Fish finders, GPS, stereo, and Power-Pole installs." },
  { icon: Droplets, title: "Routine Maintenance", desc: "Oil changes, impellers, lower unit service, and more." },
];

export default function Service() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setS(r.data || {})).catch(() => {}); }, []);
  return (
    <div data-testid="service-page">
      <Seo title="Marine Service in Bristol, TN | Yamaha & Suzuki Certified | MTM Marine" description="Certified marine service at MTM Marine in Bristol, TN. Yamaha and Suzuki outboard service, repower, winterization, and electronics." />
      <section className="relative bg-mtm-charcoal text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25"><ImageSlot label="MTM-Service-Hero-01.jpg" dimensions="" aspect="h-full" className="h-full rounded-none border-0 bg-slate-800" /></div>
        <div className="absolute inset-0 bg-gradient-to-r from-mtm-teal-dark/95 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 py-20">
          <p className="text-xs font-bold uppercase tracking-widest text-mtm-teal-bright font-mono">Bristol, Tennessee</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-heading tracking-tight mt-3">Marine Service</h1>
          <p className="text-lg text-slate-200 mt-4 max-w-2xl">{s.service_body}</p>
          <div className="mt-8"><LeadDialog ctaType="Service Inquiry" triggerLabel="Schedule Service" variant="teal" size="lg" testId="service-schedule-button" /></div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((sv) => (
            <div key={sv.title} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <span className="h-12 w-12 rounded-xl bg-mtm-teal/10 text-mtm-teal-dark flex items-center justify-center mb-4"><sv.icon size={24} /></span>
              <h3 className="font-heading font-bold text-lg text-slate-900">{sv.title}</h3>
              <p className="text-sm text-slate-600 mt-2">{sv.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-slate-200 py-14">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <ImageSlot label="MTM-Service-Bay-01.jpg" dimensions="900 × 675" />
          <div>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900">Service You Can Trust</h2>
            <ul className="mt-5 space-y-3">
              {["Certified Yamaha & Suzuki technicians", "Genuine OEM parts", "Honest estimates, no surprises", "Local, community-focused service"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-slate-700"><CheckCircle2 size={18} className="text-mtm-teal" /> {t}</li>
              ))}
            </ul>
            <div className="mt-6"><LeadDialog ctaType="Service Inquiry" triggerLabel="Request Service" variant="purple" /></div>
          </div>
        </div>
      </section>
    </div>
  );
}
